// After a deploy, a tab opened before it still asks for the old build's screen files, which the server no longer has.
// Every lazily loaded screen (73: 72 in App.tsx, 1 in ApiContext.tsx) goes through Vite's preload helper, which raises
// a cancelable `vite:preloadError` event on window when a file fails to load and rethrows the error unless the event is
// cancelled (vite 5.4 `importAnalysisBuild`: `baseModule().catch(handlePreloadError)`). Unhandled, the screen went
// blank — seen on staging 2026-10-31 after three redeploys. Here the tab reloads itself once, which fetches the new build.
//
// One reload a minute at most: if the file is still missing after reloading (a real outage, not a stale tab), the error
// is let through rather than reloading forever. If the browser refuses sessionStorage, we cannot remember that we
// already tried, so we do not reload at all.
export const CHUNK_RELOAD_KEY = 'rs:chunk-reload-at'
export const CHUNK_RELOAD_WINDOW_MS = 60_000

export interface ReloadWindow {
  addEventListener(type: 'vite:preloadError', listener: (event: Event) => void): void
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>
  location: Pick<Location, 'reload'>
}

/** Pure: whether to reload now, given when we last did (ms since epoch, or null). */
export function shouldReload(lastAt: number | null, now: number): boolean {
  return lastAt === null || !Number.isFinite(lastAt) || now - lastAt >= CHUNK_RELOAD_WINDOW_MS || now < lastAt
}

export function installChunkReload(win: ReloadWindow = window, now: () => number = Date.now): void {
  win.addEventListener('vite:preloadError', (event) => {
    const t = now()
    try {
      const raw = win.sessionStorage.getItem(CHUNK_RELOAD_KEY)
      if (!shouldReload(raw === null ? null : Number(raw), t)) return
      win.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(t))
    } catch {
      return
    }
    event.preventDefault()
    win.location.reload()
  })
}
